import React, { HTMLAttributes, useCallback, useEffect, useRef, useState } from "react"

import { IconCopy } from "@tabler/icons-react"

import { cn } from "@/lib/utils"

import { Button, ButtonProps } from "./cn/button"
import { Tooltip, TooltipProps } from "./cn/tooltip"

export interface CopyButtonProps {
  className?: string
  text: string
  tooltip?: string
  icon?: ButtonProps["icon"]
  size?: ButtonProps["size"]
  position?: "left" | "right"
  tooltipSide?: TooltipProps["side"]
}

export interface CopyToClipboardProps extends Omit<
  HTMLAttributes<HTMLSpanElement>,
  "children"
> {
  text: string
  children?: React.ReactNode
  size?: ButtonProps["size"]
  buttonPosition?: "left" | "right"
}

export const CopyToClipboard = ({
  text,
  children,
  size = "small",
  buttonPosition = "right",
  ...props
}: CopyToClipboardProps) => {
  return (
    <span {...props} className="inline-flex gap-1 align-middle">
      {buttonPosition === "left" && (
        <CopyButton text={text} size={size} position={buttonPosition} />
      )}
      {children || text}
      {buttonPosition === "right" && (
        <CopyButton text={text} size={size} position={buttonPosition} />
      )}
    </span>
  )
}

export const CopyButton = ({
  className,
  text,
  tooltip,
  icon = IconCopy,
  size = "small",
  tooltipSide = "top",
}: CopyButtonProps) => {
  const [copied, setCopied] = useState(false)
  const [open, setOpen] = useState(false)
  // Set synchronously on copy so we can ignore the tooltip's own close-on-press
  // — which fires in the same click — and keep the "Copied!" confirmation up.
  const confirming = useRef(false)
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (resetTimer.current) clearTimeout(resetTimer.current)
    },
    [],
  )

  const handleCopy = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault()
      navigator.clipboard.writeText(text)
      confirming.current = true
      setCopied(true)
      setOpen(true)
      if (resetTimer.current) clearTimeout(resetTimer.current)
      resetTimer.current = setTimeout(() => {
        // Only start closing. The label stays "Copied!" so it doesn't flip back
        // to the hint mid-fade; it resets in onOpenChangeComplete once gone.
        confirming.current = false
        setOpen(false)
      }, 1500)
    },
    [text],
  )

  return (
    <Tooltip
      open={open}
      onOpenChange={(next) => {
        // Hold the "Copied!" confirmation open through the click that triggered
        // it; otherwise mirror the tooltip's hover/focus open state.
        if (!next && confirming.current) return
        // A genuine (post-confirmation) reopen goes back to the hint.
        if (next && !confirming.current) setCopied(false)
        setOpen(next)
      }}
      // Reset the label only after the popup has fully closed, so "Copied!"
      // survives the exit animation instead of swapping mid-fade.
      onOpenChangeComplete={(isOpen) => {
        if (!isOpen) setCopied(false)
      }}
      content={copied ? "Copied!" : tooltip || "Copy to clipboard"}
      side={tooltipSide}
    >
      <Button
        className={cn("-my-1 inline align-middle", className)}
        variant="neutral"
        size={size}
        noBorder
        iconOnly
        icon={icon}
        onClick={handleCopy}
      />
    </Tooltip>
  )
}
